import type { ReactElement } from "react";
import { memo, useMemo } from "react";

import type { IDropDownItem } from "@similarweb/ui-components";
import { Dropdown, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import {
    StyledSingleSelectDDItem,
    StyledSingleSelectDDItemText,
} from "app/pages/sales-intelligence/common-components/dropdown/styles";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import useUncontrolledDropdown from "app/pages/sales-intelligence/hooks/useUncontrolledDropdown";
import JobPositionButtonLabel from "app/pages/sales-intelligence/modules/onboarding/components/JobPositionButtonLabel/JobPositionButtonLabel";
import { JOB_POSITION_ID, MAX_DD_ITEM_VALUE } from "app/pages/sales-intelligence/modules/onboarding/constants";
import { manageLongString } from "app/pages/sales-intelligence/modules/onboarding/utils";
import { DROPDOWN_AUTO_OPEN_DELAY } from "app/pages/sales-intelligence/sub-modules/signals/constants/dropdown";
import { buildDropdownSelectedItemsFromArray } from "app/pages/sales-intelligence/utils/dropdowns";

import JobPositionDropdownButton from "../JobPositionDropdownButton/JobPositionDropdownButton";

import { StyledDropdownParent } from "./styles";

type DropdownItem = Omit<IDropDownItem, "id"> & {
    id: string;
};

type JobPositionDropdownProps = {
    selectedValue: string | undefined;
    options: readonly string[];
    autoOpen?: boolean;
    onValueChange(value: string): void;
};

const JobPositionDropdown = (props: JobPositionDropdownProps) => {
    const { selectedValue, options, autoOpen = false, onValueChange } = props;

    const selectedItems: Record<string, boolean> = useMemo(
        () => buildDropdownSelectedItemsFromArray(selectedValue ? [selectedValue] : []),
        [selectedValue],
    );

    const isLongSelectedValue: boolean = useMemo(() => {
        return Boolean(selectedValue && selectedValue.length > MAX_DD_ITEM_VALUE);
    }, [selectedValue]);

    const { dropdownRef, onUncontrolledOpen } = useUncontrolledDropdown();

    useDelayedAction(DROPDOWN_AUTO_OPEN_DELAY, onUncontrolledOpen, autoOpen);

    const handleChange = (item: DropdownItem): void => {
        onValueChange(item.id);
    };

    const renderOptions = (options: readonly string[]): readonly ReactElement[] => {
        const items: ReactElement[] = [];

        options.forEach((option, index) => {
            items.push(
                <StyledSingleSelectDDItem
                    key={option}
                    id={option}
                >
                    <StyledSingleSelectDDItemText>{option}</StyledSingleSelectDDItemText>
                </StyledSingleSelectDDItem>,
            );
        });

        return items;
    };

    return (
        <Tooltip
            enabled={isLongSelectedValue}
            content={selectedValue}
        >
            <StyledDropdownParent id={JOB_POSITION_ID}>
                <Dropdown
                    ref={dropdownRef}
                    appendTo={`#${JOB_POSITION_ID}`}
                    selectedIds={selectedItems}
                    width={317}
                    onClick={handleChange}
                >
                    <JobPositionDropdownButton id="job-position-dd-button">
                        <JobPositionButtonLabel>
                            {selectedValue
                                ? manageLongString(selectedValue, MAX_DD_ITEM_VALUE)
                                : translate(`si.onboarding.job-position-dd-button.name`)}
                        </JobPositionButtonLabel>
                    </JobPositionDropdownButton>

                    {...renderOptions(options)}
                </Dropdown>
            </StyledDropdownParent>
        </Tooltip>
    );
};

export default memo(JobPositionDropdown);
